import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Local-only override of the admin-controlled "disabled region" hexagons,
 * used exclusively while a sandbox view mode is active.
 *
 * - 100% client-side: never POSTed to the backend (mirrors `sandbox-store`).
 * - Seeded once from the live map-settings value (`initFromLive`) so sandbox
 *   starts from the same picture as the live map, then diverges freely as
 *   the user toggles hexagons on/off — those toggles never touch the shared
 *   admin config.
 * - When the user leaves sandbox mode, the live `disabledRegionKeys` (from
 *   `/api/map-settings`) is used again untouched.
 */
interface SandboxRegionState {
  initialized: boolean;
  disabledRegionKeys: string[];
  /** Seeds the override from the live settings, once, if not already done. */
  initFromLive: (keys: string[]) => void;
  toggleRegion: (key: string) => void;
}

export const useSandboxRegionStore = create<SandboxRegionState>()(
  persist(
    (set, get) => ({
      initialized: false,
      disabledRegionKeys: [],

      initFromLive: (keys) => {
        if (get().initialized) return;
        set({ disabledRegionKeys: [...keys], initialized: true });
      },

      toggleRegion: (key) => {
        set((s) => {
          const has = s.disabledRegionKeys.includes(key);
          return {
            disabledRegionKeys: has
              ? s.disabledRegionKeys.filter((k) => k !== key)
              : [...s.disabledRegionKeys, key],
          };
        });
      },
    }),
    {
      name: 'warplan.sandbox-regions',
      version: 1,
      partialize: (s) => ({
        initialized: s.initialized,
        disabledRegionKeys: s.disabledRegionKeys,
      }),
    },
  ),
);
